/// <amd-module name="TFS/Admin/Views/ProjectOverview/ServicePicker" />
import React = require("react");

import AdminResources = require("TFS/Admin/Views/ProjectOverview/Resources");

import { IProductFeaturePluginService } from "TFS/Admin/Common/Util/ProductFeaturePlugin";
import { ContributedFeature, ContributedFeatureEnabledValue, ContributedFeatureState } from "TFS/FeatureManagement/Contracts/FeatureManagement";
import { IFeatureUpdateError, ITfsFeatureService } from "TFS/FeatureManagement/TfsFeatureService";
import { ITfsPageService } from "Tfs/Platform/Page";
import { format } from "VSS/Core/Util/String";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Components, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { Dialog } from "VSSUI/Dialog";
import { Image } from "VSSUI/Image";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Spinner } from "VSSUI/Spinner";
import { Toggle } from "VSSUI/Toggle";
import { ITooltipProps } from "VSSUI/TooltipEx";

interface IFeatureStates {
    [featureId: string]: ContributedFeatureState;
}

interface IServicePickerComponentState {
    serviceFeatures?: ContributedFeature[];
    featureStates?: IFeatureStates;
    featureChangesInProgress: { [featureId: string]: boolean };
    /** show a dialog to confirm disabling this feature */
    confirmDisableService?: ContributedFeature;
    disableExtraContent?: JSX.Element;
    message?: string;
    messageBarType?: MessageBarSeverity;
    canManage?: boolean;
}

export class ServicePickerComponent extends VssComponent<{}, IServicePickerComponentState> {
    private _featureService: ITfsFeatureService;

    public constructor(props: {}, context?: any) {
        super(props, context);

        this._featureService = this.context.pageContext.getService<ITfsFeatureService>("ITfsFeatureService");

        this.state = {
            featureChangesInProgress: {}
        };
    }

    public componentDidMount(): void {
        this._loadFeatures();
        this._featureService.subscribe(this._onFeatureStateChanged, "featureStateUpdated");
        this._featureService.subscribe(this._onFeatureStateError, "featureStateUpdateError");
    }

    public componentWillUnmount(): void {
        this._featureService.unsubscribe(this._onFeatureStateChanged, "featureStateUpdated");
        this._featureService.unsubscribe(this._onFeatureStateError, "featureStateUpdateError");
    }

    public render(): JSX.Element {
        const services = this.state.serviceFeatures || [];
        const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const project = pageService.getData()!.project!;

        return (
            <div className="service-picker">
                {this.state.message ? (
                    <MessageBar
                        severity={this.state.messageBarType || MessageBarSeverity.Info}
                        onDismiss={() => this.setState({ message: undefined })}
                    >
                        {this.state.message}
                    </MessageBar>
                ) : null}
                <div className="title-m service-title">{AdminResources.AvailableServicesHeading}</div>
                <div className="service-list flex flex-column">{services.map(s => this._renderToggle(s))}</div>
                {this.state.confirmDisableService ? (
                    <Dialog
                        className="servicePicker-ConfirmDialog"
                        titleProps={{ text: AdminResources.RemoveServiceDialogTitle }}
                        onDismiss={() => this.setState({ confirmDisableService: undefined, disableExtraContent: undefined })}
                        footerButtonProps={[
                            {
                                text: AdminResources.Cancel,
                                onClick: () => this.setState({ confirmDisableService: undefined, disableExtraContent: undefined })
                            },
                            {
                                primary: true,
                                text: format(AdminResources.RemoveServiceButton, this.state.confirmDisableService.name),
                                onClick: () => this._onRemoveConfirmed()
                            }
                        ]}
                    >
                        {this.state.disableExtraContent}
                        <div>{format(AdminResources.RemoveServiceDialogText, this.state.confirmDisableService.name)}</div>
                    </Dialog>
                ) : null}
            </div>
        );
    }

    private _onFeatureStateChanged = (featureState: ContributedFeatureState) => {
        this.setState((prevState, props) => ({
            featureStates: {
                ...prevState.featureStates,
                [featureState.featureId]: featureState
            },
            featureChangesInProgress: {
                ...prevState.featureChangesInProgress,
                [featureState.featureId]: false
            },
            message: AdminResources.ChangesMade,
            messageBarType: MessageBarSeverity.Info
        }));
    };

    private _onFeatureStateError = (featureUpdateError: IFeatureUpdateError) => {
        this.setState((prevState, props) => ({
            featureChangesInProgress: {
                ...prevState.featureChangesInProgress,
                [featureUpdateError.feature.id]: false
            },
            message: featureUpdateError.error.message,
            messageBarType: MessageBarSeverity.Error
        }));
    };

    private async _loadFeatures(): Promise<void> {
        const definitions = await this._featureService.getFeatureDefinitions("ms.vss-tfs-web.product-features", true, ["product"]);
        const canManage = !!definitions.managementScopes["project"];
        const serviceFeatures = [...(definitions.features || [])];
        serviceFeatures.sort((a, b) => (a.order !== undefined ? a.order : 9999) - (b.order !== undefined ? b.order : 9999));
        const featureStates = await this._getFeatureStates(serviceFeatures);

        this.setState({ serviceFeatures, featureStates, canManage });
    }

    private async _getFeatureStates(features: ContributedFeature[]): Promise<IFeatureStates> {
        const featureStateArray = await Promise.all(
            this._featureService.getFeatureStates(features, false, "project", { project: this._getProjectId() })
        );
        return Object.assign({}, ...featureStateArray) as IFeatureStates;
    }

    private _renderToggle(serviceFeature: ContributedFeature): JSX.Element {
        const state = this.state.featureStates![serviceFeature.id];
        const canManage = this.state.canManage;
        const updateInProgress = this.state.featureChangesInProgress[serviceFeature.id] || false;
        const iconAsset = serviceFeature && serviceFeature._links && serviceFeature._links["icon"];
        const iconUrl = iconAsset && getAssetLocation(this.context.pageContext, iconAsset.href);
        const tooltipProps: ITooltipProps | undefined = state.reason ? { text: state.reason } : undefined;

        return (
            <div className="service flex flex-row flex-grow" key={serviceFeature.id} data-featureid={serviceFeature.id}>
                <Image src={iconUrl} className="service-icon flex-self-center" alt={serviceFeature.name + " icon"} />
                <div className="text flex flex-column scroll-hidden">
                    <div className="name fontWeightSemiBold">{serviceFeature.name}</div>
                    <div className="description">{serviceFeature.description}</div>
                </div>
                <div className="right flex-self-center">
                    {state.state !== ContributedFeatureEnabledValue.Undefined && !updateInProgress ? (
                        <div>
                            <Toggle
                                checked={state.state === ContributedFeatureEnabledValue.Enabled}
                                disabled={!canManage || state.overridden}
                                tooltipProps={tooltipProps}
                                className="toggle"
                                offText={AdminResources.ServiceDisabledToggleLabel}
                                onText={AdminResources.ServiceEnabledToggleLabel}
                                onAriaLabel={serviceFeature.name}
                                offAriaLabel={serviceFeature.name}
                                onChange={(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, value: boolean) =>
                                    this._onToggleChanged(serviceFeature, value)
                                }
                            />
                        </div>
                    ) : (
                        <Spinner />
                    )}
                </div>
            </div>
        );
    }

    private _onToggleChanged = (serviceFeature: ContributedFeature, enabled: boolean): void => {
        if (enabled) {
            this._setServiceEnabled(serviceFeature, true);
        } else {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService
                .getChildServices<IProductFeaturePluginService>(serviceFeature.id, "ms.vss-tfs-web.product-feature-plugin")
                .then(plugins => {
                    if (plugins.length > 0) {
                        plugins[0].service.getFeatureDisableMessage(serviceFeature).then(content => {
                            this.setState({ confirmDisableService: serviceFeature, disableExtraContent: content });
                        });
                    } else {
                        this.setState({ confirmDisableService: serviceFeature, disableExtraContent: undefined });
                    }
                });
        }
    };

    private _onRemoveConfirmed(): void {
        // this should always be the case, but just to make TypeScript happy...
        if (this.state.confirmDisableService) {
            this._setServiceEnabled(this.state.confirmDisableService, false);
            this.setState({ confirmDisableService: undefined, disableExtraContent: undefined });
        }
    }

    private _setServiceEnabled = (serviceFeature: ContributedFeature, enabled: boolean) => {
        this.setState((prevState, props) => ({
            featureChangesInProgress: {
                ...prevState.featureChangesInProgress,
                [serviceFeature.id]: true
            }
        }));

        const newValue = enabled ? ContributedFeatureEnabledValue.Enabled : ContributedFeatureEnabledValue.Disabled;
        this._featureService.setFeatureState(serviceFeature, false, "project", this._getProjectId(), newValue).then(() => {
            // First set the udpated value and then query for states.  This is needed because disabling one state
            // may automatically disable another state.  It is also possible that the call to fetch the updated states
            // goes to an AT whose cache has not yet been invalidated from the original call to update the state.
            // So check that the returned states match the value from the original call.  If not, retry a few times.
            let retryCount = 10;
            const fetchUpdatedStates = () => {
                // Refresh all feature states since toggling one feature may affect another's state
                this._getFeatureStates(this.state.serviceFeatures!).then(featureStates => {
                    if (featureStates && featureStates[serviceFeature.id] && featureStates[serviceFeature.id].state === newValue) {
                        this.setState({ featureStates });
                    } else {
                        if (--retryCount > 0) {
                            fetchUpdatedStates();
                        }
                    }
                });
            };

            fetchUpdatedStates();
        });
    };

    private _getProjectId(): string {
        const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();
        return pageData!.project!.id;
    }
}

Components.add("servicePicker", ServicePickerComponent);
